import Card from '@/components/feature/parking/card';
import ServiceGuide from '@/components/feature/parking/service-guide';
import FeeStandardContent from './FeeStandardContent';
import PurchaseAmountDiscountContent from './PurchaseAmountDiscountContent';
import CouponInfoContent from './CouponInfoContent';
import ParkingLocationRule from './ParkingLocationRule';
import type { BranchParkingConfig } from '@/services/dto/parking';
import { useTranslation } from 'react-i18next';

interface ParkingContentProps {
    parkingData?: BranchParkingConfig | null;
    isHorizontal?: boolean;
}

export default function ParkingContent({ parkingData, isHorizontal }: ParkingContentProps) {
    const { t } = useTranslation();
    const {
        feeStdList = [],
        pchAmtDcStdList = [],
        cuterPktckYn,
        freePktckPrvdYn,
        pafeeDcYn,
        extnParklotPrvdYn,
        cuterPktckTm,
        freePktckTm,
        freePktckPrvdSppr,
    } = parkingData || {};
    const isCardTwoDisplay = cuterPktckYn === 'Y' || freePktckPrvdYn === 'Y' || pafeeDcYn === 'Y';
    const isCardFourDisplay = extnParklotPrvdYn === 'Y';
    const freeCouponTitle = t('parking.freeCouponTitle');
    const titleMapping = {
        feeStandard: t('parking.title.feeStandard'),
        purchaseAmountDiscount: t('parking.title.purchaseAmountDiscount'),
        couponInfo: t('parking.title.couponInfo'),
        parkingLocationRule: t('parking.title.parkingLocationRule'),
    };
    // Create mapping array for fields with value 'Y'
    const createCardTwoDataMapping = () => {
        const mappingArray = [];

        if (cuterPktckYn === 'Y') {
            mappingArray.push({
                type: 'cuterPktck',
                title: (t('parking.culturalCenterParkingTicket')),
                data: {
                    time: cuterPktckTm,
                    ticket: '',
                },
            });
        }

        if (freePktckPrvdYn === 'Y') {
            mappingArray.push({
                type: 'freePktckPrvd',
                title: (t('parking.freeParkingTicket')),
                data: {
                    time: freePktckTm,
                    ticket: freePktckPrvdSppr,
                },
            });
        }

        if (pafeeDcYn === 'Y') {
            mappingArray.push({
                type: 'pafeeDc',
                title: (t('parking.prepaidParkingDiscountTicket')),
                data: {
                    time: '',
                    ticket: '',
                },
            });
        }

        return mappingArray;
    };

    const cardTwoData = createCardTwoDataMapping();

    const ariaLabelMapping = {
        feeStandard: feeStdList.map((fee) => fee.prkTm + ' ' + fee.prkAmt + t('parking.unit.won')).join(', '),
        couponInfo: cardTwoData
            .map(
                (item) =>
                    item.title +
                    ' ' +
                    item.data.time +
                    `${item.data.time ? t('parking.unit.hour') : ''}` +
                    ' ' +
                    item.data.ticket +
                    `${item.data.ticket ? t('parking.unit.ticket') : ''}`
            )
            .join(', '),
        purchaseAmountDiscount: pchAmtDcStdList.map((discount) => discount.pchAmt + t('parking.unit.won') + ' ' + discount.dcTm).join(', '),
        parkingLocationRule: t('parking.rules.parkingLocationRule'),
    };

    return (
        <>
            <ServiceGuide />
            {/* Cards */}
            <div
                className="widescreen:grid-cols-2 widescreen:gap-x-[calc(60*var(--vw-unit))] widescreen:gap-y-[calc(24*var(--vh-unit))] grid grid-cols-1 gap-[calc(60*var(--vw-unit))]"
                data-region-nav
                data-region-order={3}
            >
                {/* Card 1 - Always first */}
                {feeStdList && feeStdList.length > 0 && (
                    <Card
                        ariaLabel={titleMapping.feeStandard + ' ' + ariaLabelMapping.feeStandard}
                        title={titleMapping.feeStandard}
                        content={<FeeStandardContent feeStdList={feeStdList} />}
                    />)}
                {isHorizontal ? (
                    // Horizontal layout: 1-2-3-4
                    <>
                        {/* Card 2 - Second in horizontal */}
                        {isCardTwoDisplay && (
                            <Card
                                ariaLabel={titleMapping.couponInfo + ' ' + ariaLabelMapping.couponInfo}
                                title={titleMapping.couponInfo}
                                content={<CouponInfoContent cardTwoData={cardTwoData} isCardTwoDisplay={isCardTwoDisplay} />}
                            />
                        )}

                        {/* Card 3 - Third in horizontal */}
                        {pchAmtDcStdList && pchAmtDcStdList.length > 0 && (
                            <Card
                                ariaLabel={titleMapping.purchaseAmountDiscount + ' ' + ariaLabelMapping.purchaseAmountDiscount}
                                title={titleMapping.purchaseAmountDiscount}
                                content={<PurchaseAmountDiscountContent pchAmtDcStdList={pchAmtDcStdList} />}
                            />)}

                        {/* Card 4 - Fourth in horizontal */}
                        {isCardFourDisplay && (
                            <Card
                                ariaLabel={titleMapping.parkingLocationRule + ' ' + ariaLabelMapping.parkingLocationRule}
                                title={titleMapping.parkingLocationRule}
                                content={<ParkingLocationRule />}
                            />
                        )}
                    </>
                ) : (
                    // Vertical layout: 1-3-2-4
                    <>
                        {/* Card 3 - Second in vertical */}
                        <Card
                            ariaLabel={titleMapping.purchaseAmountDiscount + ' ' + ariaLabelMapping.purchaseAmountDiscount}
                            title={titleMapping.purchaseAmountDiscount}
                            content={<PurchaseAmountDiscountContent pchAmtDcStdList={pchAmtDcStdList} />}
                        />

                        {/* Card 2 - Third in vertical */}
                        {isCardTwoDisplay && (
                            <Card
                                ariaLabel={titleMapping.couponInfo + ' ' + ariaLabelMapping.couponInfo}
                                title={titleMapping.couponInfo}
                                content={<CouponInfoContent cardTwoData={cardTwoData} isCardTwoDisplay={isCardTwoDisplay} />}
                            />
                        )}

                        {/* Card 4 - Fourth in vertical */}
                        {isCardFourDisplay && (
                            <Card
                                ariaLabel={titleMapping.parkingLocationRule + ' ' + ariaLabelMapping.parkingLocationRule}
                                title={titleMapping.parkingLocationRule}
                                content={<ParkingLocationRule />}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );
}
